import { ArticleSummary, ClientData, ManagedSource } from '../types';
import { isEmbedded } from './embedMode';
import { buildArticlesFeedPage, FeedFixtureRow } from './articlesFeedMock';
import { buildManagedSourcesPage } from './managedSourcesMock';
import { normalizeFeedRequest, normalizeFeedRequestId } from './feedRequest';
import { normalizeSourceListQuery } from './sourceListQuery';
import { validateAddSourceInput } from './addSourceInput';

type Handler = (...args: unknown[]) => void;

declare global {
  interface Window {
    // COM-636 test-only hook. Some specs (OnboardingGate auto-open coverage)
    // need the fixture project to be in the "never configured" state that
    // actually triggers `shouldPromptForClientData` — i.e. `companyType: []`
    // — rather than today's already-configured fixture. An init script sets
    // this before the app boots, mirroring how `__EMBED_BOOTSTRAP__` is
    // injected (embedMode.ts). Defaults to false/undefined so every other
    // spec keeps the configured fixture below unless it explicitly opts in;
    // this must never leak into unrelated specs.
    __E2E_EMPTY_COMPANY_TYPE__?: boolean;
    // COM-637 task 8 (2026-08-15): opts `startSourceDiscovery` into
    // simulating a per-source discovery failure - an LLM-proposed URL that
    // turns out not to exist, routine on most real runs - between
    // `autoCompleteStarted` and `autoCompleteFinished`, instead of firing
    // both back to back. Exists so the "a per-source error must not close
    // the progress dialog while the run continues" behaviour has something
    // to exercise; defaults to false/undefined so every other spec keeps
    // today's instant started/finished pair.
    __E2E_DISCOVERY_SOURCE_ERROR__?: boolean;
    // COM-638 task 5: makes `getArticlesFeed` answer with zero rows
    // regardless of the request, while leaving the project itself fully
    // configured (unlike `__E2E_EMPTY_COMPANY_TYPE__`, which also flips
    // `shouldPromptForClientData` true and pops the onboarding wizard over
    // the page under test). Exists so the All Articles page's own
    // "no articles yet" EmptyState + add-source CTA can be exercised without
    // that unrelated wizard getting in the way. Defaults to false/undefined
    // so every other spec keeps the full 25-article fixture.
    __E2E_EMPTY_ARTICLES_FEED__?: boolean;
    // COM-640 Task 3 hook: opts `addSource` into simulating the backend's
    // duplicate-URL refusal (`socket.emit('error', { message: 'This URL
    // already saved' })` in backend/src/index.ts's `addSource` handler)
    // instead of the success path below - Task 3 needs this to exercise "a
    // refused add must not strand the scanning indicator" and "the dialog
    // stays open and shows the refusal" end to end. Defaults to
    // false/undefined so every other spec keeps today's always-succeeds
    // behaviour.
    __E2E_ADD_SOURCE_DUPLICATE__?: boolean;
    // COM-647 Task 5: same reasoning/shape as `__E2E_EMPTY_ARTICLES_FEED__`
    // above, for the Sources Settings table's own `getManagedSources` -
    // answers with zero rows/total regardless of the request, so the page's
    // genuine "No sources yet" EmptyState (not the "no results match your
    // filters" variant a search/filter narrowing to zero would produce) can
    // be exercised without deleting all 33 fixture rows through the UI first.
    // Defaults to false/undefined so every other spec keeps the full fixture.
    __E2E_EMPTY_MANAGED_SOURCES__?: boolean;
    // Fix wave (2026-08-21, review Important 1): flips the fixture project's
    // own `role` to 'VIEWER' instead of the default 'OWNER' - lets the
    // Sources Settings page's read-only-for-a-VIEWER behaviour
    // (`SourcesSettingsPage.tsx`'s `canManage`) be exercised end to end.
    // Global rather than per-request because `canManageProject()`
    // (SourcesContext.tsx) reads the CURRENT selected project's role off
    // `projects`, which this same fixture backs for every project-scoped
    // socket event - there is only ever one fixture project in this mock, so
    // there is nothing to flip "just for one call". Defaults to
    // false/undefined so every other spec keeps today's OWNER fixture.
    __E2E_VIEWER__?: boolean;
    // Fix wave (2026-08-21, gap 3): opts `deleteSources` into reporting
    // `deletedCount: 0` for a batch of otherwise-valid ids, WITHOUT actually
    // removing them from `managedSourcesState` - the mock's stand-in for the
    // real backend's documented race (`dbService.ts#deleteSources`'s own
    // comment): a co-owner already deleted the selected row(s) between the
    // confirm dialog and this reply. Exists so a pinning e2e test can drive
    // the "0 sources deleted" info-toast branch (SourcesSettingsPage.tsx's
    // `confirmDelete`) AND the rows-stay-put behaviour through the real UI,
    // neither of which the existing delete test can reach (it only ever
    // deletes rows that genuinely exist, where old and new code agree).
    // Defaults to false/undefined so every other spec keeps today's
    // deletes-what-you-asked-for behaviour.
    __E2E_STALE_DELETE__?: boolean;
  }
}

const wantsEmptyCompanyType = typeof window !== 'undefined' && window.__E2E_EMPTY_COMPANY_TYPE__ === true;
const wantsDiscoverySourceError = typeof window !== 'undefined' && window.__E2E_DISCOVERY_SOURCE_ERROR__ === true;
const wantsEmptyArticlesFeed = typeof window !== 'undefined' && window.__E2E_EMPTY_ARTICLES_FEED__ === true;
const wantsAddSourceDuplicate = typeof window !== 'undefined' && window.__E2E_ADD_SOURCE_DUPLICATE__ === true;
const wantsEmptyManagedSources = typeof window !== 'undefined' && window.__E2E_EMPTY_MANAGED_SOURCES__ === true;
const wantsViewerRole = typeof window !== 'undefined' && window.__E2E_VIEWER__ === true;
const wantsStaleDelete = typeof window !== 'undefined' && window.__E2E_STALE_DELETE__ === true;

// Fix wave (2026-08-21, gap 1): copied verbatim from
// `backend/src/lib/projectAccess.ts`'s `NOT_OWNER_MESSAGE` - the real message
// every `requireProjectOwner`-gated handler refuses a non-OWNER caller with.
// Can't import it directly (frontend and backend are separate builds/
// packages), so it's mirrored here the same way `'This URL already saved'`
// and friends already are elsewhere in this file. Used by every one of this
// page's four mutation cases below when `__E2E_VIEWER__` is set, so the mock
// actually models "read allowed, mutations refused" instead of just flipping
// a cosmetic `role` field nothing downstream of `getManagedSources` ever
// checks.
const VIEWER_MUTATION_REFUSED_MESSAGE = 'You must be a project owner to perform this action';

// COM-638 fix: `updateArticleStatus`'s isNew:false flip used to mutate
// `fixtureArticleFeedRawRows` IN PLACE, which only ever survives within the
// current module instance - a `page.reload()` re-evaluates this whole file
// from scratch, so the mutation was silently lost and the reload half of the
// e2e persistence assertion failed (`isNew: true` came back from the fresh
// fixture). sessionStorage survives a same-tab reload but is still isolated
// per browser context, so it resets between Playwright tests (each gets its
// own context) without any manual cleanup and without leaking into other
// specs the way a module-level `let` reset only on next import would not.
//
// Divergence from the real backend, documented rather than hidden: the real
// `updateArticleStatus` clears isNew per (source, article) row - a
// shared-url article can carry a different isNew per source. This mock
// clears by `articleUrl` ALONE. That's deliberately simpler and still
// correct for what this fixture needs: `dedupeFeedRows` (articlesFeedMock.ts)
// already collapses a shared url down to one dedup-winner row before the UI
// ever sees it, so url-alone is precisely "the row the user could have
// acted on" - there is no reachable case here where per-source precision
// would produce a different visible result.
const READ_ARTICLE_URLS_STORAGE_KEY = '__e2e_read_article_urls__';

function readClearedArticleUrls(): Set<string> {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return new Set();
  }
  try {
    const raw = window.sessionStorage.getItem(READ_ARTICLE_URLS_STORAGE_KEY);
    return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
  } catch {
    // Malformed storage (e.g. hand-edited by a test) degrades to "nothing
    // cleared yet" rather than throwing and breaking every feed request.
    return new Set();
  }
}

function recordArticleUrlRead(url: string): void {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }
  const cleared = readClearedArticleUrls();
  cleared.add(url);
  window.sessionStorage.setItem(READ_ARTICLE_URLS_STORAGE_KEY, JSON.stringify(Array.from(cleared)));
}

// Kept intentionally close to the shape of the internal `Project` interface
// declared (not exported) in SourcesContext.tsx.
const fixtureProject = {
  id: 'p1',
  name: 'Demo project',
  role: wantsViewerRole ? 'VIEWER' : 'OWNER',
  gptModel: '4',
  createdAt: '2026-01-01T00:00:00.000Z',
  updatedAt: '2026-01-01T00:00:00.000Z',
  companyType: wantsEmptyCompanyType ? [] : ['Bank'],
  jurisdictions: ['European Union'],
  topics: ['KYC / AML'],
  documentTypes: ['Final Regulations'],
  notificationFormat: [] as string[],
};

// COM-638: fixture data for the All Articles feed (`getArticlesFeed` /
// `articlesFeedLoaded`). Its own fixture sources, separate from the
// add-source flow above (COM-638 task 8 removed that flow's `sources`
// list/fixture entirely as dead code) - the feed spans several sources on
// purpose, so dedup across sources has something real to do.
//
// COM-639: `status` added - fs4 is deliberately `in_progress` (not yet
// `completed`, mirroring `sourcesService.ts`'s status lifecycle), kept that
// way for realism even though it no longer drives dropdown inclusion.
//
// Fix wave (2026-08-18, Artur): the Sources filter dropdown's rule used to
// be "active (`completed`) sources only", which this in_progress status
// existed to get excluded by. Overridden, and refined again since - the
// dropdown's universe is now the three-case rule `shouldOfferSourceInFilter`
// (frontend/src/lib/filterSources.ts) implements: exclude only a source
// that has BOTH an error AND zero articles - see SourcesContext.tsx's
// `fetchFilterSources` doc comment for why (the feed itself has no
// source-status predicate, so a status-filtered dropdown silently excluded
// an errored/in-progress source's already-visible articles from any
// explicit selection). fs4 HAS articles (6, via its slot in the rotation
// below), so it correctly appears in the dropdown DESPITE being
// in_progress - the exclusion case this fixture set exercises is
// `zeroArticleFixtureSource` (fs6) further below instead (paired with
// `newlyAddedFixtureSource`, fs7, which proves zero articles alone is not
// enough to exclude). This is purely a `getSources` fixture concern - it
// does not affect `getArticlesFeed`'s dedup/attribution rows below, which
// only ever read `id`/`name`/`url`.
const feedFixtureSources = [
  { id: 'fs1', name: 'EBA Updates', url: 'https://example.org/eba', status: 'completed' },
  { id: 'fs2', name: 'ESMA Updates', url: 'https://example.org/esma', status: 'completed' },
  { id: 'fs3', name: 'FCA Updates', url: 'https://example.org/fca', status: 'completed' },
  { id: 'fs4', name: 'BaFin Updates', url: 'https://example.org/bafin', status: 'in_progress' },
];

// COM-639: an extra fixture source that never appears in the feed's
// per-article rotation below (`feedFixtureSources.length` - used as the
// modulo divisor that assigns each of the 25 feed articles a source - stays
// 4, so this addition can't reshuffle that existing fixture) but DOES
// appear in the `getSources` fixture list right after it: a source with an
// EMPTY name, so the dropdown's link-domain fallback for unnamed sources has
// a real fixture to render. `articles` (fix wave) is its own fake one-entry
// array, unconnected to `getArticlesFeed`'s rotation - a source's full
// articles list and what a filtered feed window actually surfaces are two
// independent things on the real backend too - needed so this source still
// clears the dropdown's inclusion predicate (`shouldOfferSourceInFilter`).
const unnamedFixtureSource = {
  id: 'fs5',
  name: '',
  url: 'https://example.org/unnamed-regulator-feed',
  status: 'completed',
  articles: [{ url: 'https://example.org/unnamed-regulator-feed/announcement-1' }],
};

// COM-639 fix wave: the dropdown-exclusion fixture. `status: 'error'` is
// load-bearing now, not arbitrary: the current predicate
// (`shouldOfferSourceInFilter`, frontend/src/lib/filterSources.ts) excludes
// a source only when it has BOTH an error AND zero articles - this fixture
// is the one case that satisfies both halves at once. Never in the feed's
// own rotation (`feedFixtureSources.length` stays 4), so it contributes zero
// rows to any filtered result. See `newlyAddedFixtureSource` (fs7) below for
// the fixture that proves zero articles ALONE is not enough to exclude.
const zeroArticleFixtureSource = {
  id: 'fs6',
  name: 'Zero Article Source',
  url: 'https://example.org/zero-article-source',
  status: 'error',
  articles: [] as { url: string }[],
};

// COM-639 fix wave: the dropdown-inclusion-despite-zero-articles fixture -
// the counterpart to `zeroArticleFixtureSource` (fs6) above. `status:
// 'pending'` (no error) with zero articles: a freshly added/still-scanning
// source the predicate must still offer a checkbox for, since the user
// needs to be able to filter by it even before it has produced anything.
// Never in the feed's own rotation (`feedFixtureSources.length` stays 4),
// so it contributes zero rows to any filtered result and no feed count
// changes.
const newlyAddedFixtureSource = {
  id: 'fs7',
  name: 'Newly Added Source',
  url: 'https://example.org/newly-added-source',
  status: 'pending',
  articles: [] as { url: string }[],
};

// Fixed fixture value (not `new Date()`) for determinism, same reasoning as
// `FEED_BASE_CREATED_AT` below.
const FIXTURE_SOURCE_CREATED_AT = '2026-01-01T00:00:00.000Z';

// 25 distinct urls - `feedFixtureTitles[0]` is deliberately picked up by two
// different fixture sources below, so the feed's `DISTINCT ON (a.url)` dedup
// rule has something real to collapse.
const feedFixtureTitles = [
  'EU adopts new AML directive amendments',
  'ESMA publishes guidance on sustainability disclosures',
  'FCA fines bank for KYC failures',
  'BaFin updates reporting thresholds for payment institutions',
  'European Central Bank raises capital requirements for tier-2 banks',
  'UK Treasury consults on crypto-asset regulation',
  'ESMA clarifies MiFID II best execution rules',
  'EBA issues opinion on operational resilience testing',
  'FCA proposes changes to consumer duty rules',
  'BaFin warns on unlicensed crypto exchanges',
  'European Commission proposes AMLA headquarters decision',
  'FCA publishes final rules on payment services',
  'ESMA updates prospectus regulation technical standards',
  'EBA guidelines on ICT risk management',
  'BaFin circular on outsourcing arrangements',
  'FCA handbook notice 112',
  'European Parliament votes on DORA implementation timeline',
  'ESMA peer review on short selling regulation',
  'EBA stress test methodology 2026',
  'FCA sets out expectations for consumer credit firms',
  'BaFin guidance on sustainability-linked loans',
  'European Banking Authority consults on AML/CFT guidelines',
  'FCA reprimands firm for market abuse controls failure',
  'ESMA publishes Q&A on benchmarks regulation',
  'BaFin announces new reporting portal for insurers',
];

// Fixed base timestamp (not `Date.now()`) so the fixture stays deterministic
// across runs/days instead of drifting with the system clock.
const FEED_BASE_CREATED_AT = Date.parse('2026-08-01T09:00:00.000Z');

function feedCreatedAt(index: number): string {
  return new Date(FEED_BASE_CREATED_AT - index * 8 * 60 * 60 * 1000).toISOString();
}

// Index of 'FCA publishes final rules on payment services' - the one
// article whose `updateDate` is later than its `createdAt`, exercising the
// `GREATEST(createdAt, updateDate)` sortDate rule instead of the common
// (`updateDate: null` -> sortDate falls back to createdAt) case.
const FEED_UPDATE_LATER_INDEX = 11;

// Several articles are new (`isNew: true`), scattered rather than clustered.
const FEED_IS_NEW_INDICES = new Set([0, 2, 4, 7, 10, 13, 16, 19, 22]);

const fixtureArticleFeedRows: FeedFixtureRow[] = feedFixtureTitles.map((title, index) => {
  const source = feedFixtureSources[index % feedFixtureSources.length];
  const createdAt = feedCreatedAt(index);
  const updateDate =
    index === FEED_UPDATE_LATER_INDEX
      ? new Date(Date.parse(createdAt) + 30 * 60 * 60 * 1000).toISOString()
      : null;

  return {
    url: `https://example.org/articles/feed-${index + 1}`,
    title,
    createdAt,
    updateDate,
    relevance: `Relevant because it discusses ${title.toLowerCase()}`,
    isNew: FEED_IS_NEW_INDICES.has(index),
    sourceId: source.id,
    sourceName: source.name,
    sourceUrl: source.url,
  };
});

// The dedup case: a second `source_articles` row for the very first url,
// from a different fixture source. Same article fields (title/createdAt/
// updateDate live on the shared `articles` row, not the join row) - only the
// source binding (and its relevance/isNew) differs. Both rows share the same
// sortDate, so `fs1` (the lower sourceId) is the deterministic dedup winner.
const fixtureArticleFeedDuplicateRow: FeedFixtureRow = {
  ...fixtureArticleFeedRows[0],
  relevance: 'Also flagged as relevant by ESMA',
  isNew: false,
  sourceId: feedFixtureSources[1].id,
  sourceName: feedFixtureSources[1].name,
  sourceUrl: feedFixtureSources[1].url,
};

// 26 raw rows -> 25 articles after dedup, so the default limit of 20 needs
// three pages (20 + 5) to page through the whole feed.
const fixtureArticleFeedRawRows: FeedFixtureRow[] = [
  ...fixtureArticleFeedRows,
  fixtureArticleFeedDuplicateRow,
];

// COM-639 fix wave: full fixture source list for `getSources` / `sourcesLoaded` -
// unlike `feedFixtureSources` above (scoped to the feed's own dedup fixture
// needs), this is everything the Sources filter dropdown's `fetchFilterSources`
// (SourcesContext.tsx) should see: with articles and without, named and
// unnamed. fs1-fs4 get their REAL per-source `articles` grouped straight out
// of `fixtureArticleFeedRawRows` - the SAME rows `getArticlesFeed` serves -
// instead of a second hand-authored count, so the two can never drift apart.
// `unnamedFixtureSource` (fs5), `zeroArticleFixtureSource` (fs6), and
// `newlyAddedFixtureSource` (fs7) already carry their own explicit
// `articles` (see above), since none of them participates in the feed's own
// rotation.
const allFixtureSources = [
  ...feedFixtureSources.map((source) => ({
    ...source,
    articles: fixtureArticleFeedRawRows
      .filter((row) => row.sourceId === source.id)
      .map((row) => ({ url: row.url })),
  })),
  unnamedFixtureSource,
  zeroArticleFixtureSource,
  newlyAddedFixtureSource,
];

// COM-640: one extra source + one extra feed row per successful `addSource`
// call this session - module-scoped state, not sessionStorage-backed like
// `READ_ARTICLE_URLS_STORAGE_KEY` above: unlike that flip, nothing here
// needs to survive a `page.reload()`, so a fresh module instance per test is
// exactly the right lifetime. `addedSourceSeq` (rather than a fixed id/url)
// so a spec that adds more than once doesn't silently collide.
let addedSourceSeq = 0;
const addedSourcesLoadedPayload: unknown[] = [];
const addedFeedRows: FeedFixtureRow[] = [];

// COM-640 (semantics reversed 2026-08-19): the real backend's `lastUpdatedAt`
// is no longer `max(article.created_at)` - it is `max(sources.last_checked_at)`
// (`DatabaseService#getLastCheckedAt`), stamped when a source's scan reaches a
// terminal status, NOT when an article happens to be found. This mock has no
// separate per-source "checked at" fixture to aggregate over (the fixture
// sources never scan on their own - only `addSource` below simulates a real
// scan completing), so it approximates the same aggregate using each feed
// row's own `createdAt` as a stand-in for "when its source last checked":
// every fixture row exists only because some source found it, and the one row
// this mock ever adds post-load (`addSource`'s `scannedAt` below) is stamped
// at the moment that simulated scan actually completes - exactly the real
// `lastCheckedAt` moment, just carried on the row instead of a separate
// per-source field. `null` (no rows at all) is the "nothing has ever been
// checked" case, matching a project whose sources have never completed a
// check.
function latestCheckTimeAmongRows(rows: FeedFixtureRow[]): string | null {
  if (rows.length === 0) {
    return null;
  }
  return rows.reduce((latest, row) => (row.createdAt > latest ? row.createdAt : latest), rows[0].createdAt);
}

// COM-640 test-determinism fix: a fixed 3-hour offset from "now", evaluated
// fresh on every `getArticlesFeed` request - NOT derived from the fixture
// rows' own `createdAt` values, unlike every other timestamp in this file.
// WHY: every fixture row's `createdAt` is a frozen calendar date
// (`FEED_BASE_CREATED_AT` etc., chosen for determinism), but the UI renders
// `lastUpdatedAt` through a MOVING relative label (relativeTime.ts's
// `formatLastUpdated`, computed against the real wall clock via dayjs
// `.from()`). Feeding a frozen fixture date into that label means the "ago"
// count grows by about a day every day this suite runs, so it - and every
// screenshot baseline that shows it (all-articles-populated,
// all-articles-heading-row-idle/-scanning) - silently goes stale on a
// schedule with nobody having touched any code (already observed once, see
// docs/superpowers/specs/2026-08-18-com640-refresh-scanning-design.md's
// As-built note). A now-relative offset renders the exact same label
// ("Last updated 3 hours ago" - also the literal example text that spec and
// Figma 2871:49860 use) on every run, on every date, forever. This reasoning
// is unaffected by the "newest article" -> "last check" semantics reversal
// above: either way the idle floor needs to be now-relative, not calendar-
// fixed, to avoid drifting baselines.
//
// Do NOT "fix" this back to a pure fixture-row derivation - that
// reintroduces the drift. This only replaces the FLOOR the idle label reads
// when no source has been added yet: `latestCheckTimeAmongRows` below still
// takes the max against `addSource`'s added rows (real `Date.now()`), so
// the "label moves once the scan finishes" behaviour this mock exists to
// demo is unaffected.
const MOCK_LAST_CHECKED_OFFSET_MS = 3 * 60 * 60 * 1000;

function mockLastCheckedFloor(): string {
  return new Date(Date.now() - MOCK_LAST_CHECKED_OFFSET_MS).toISOString();
}

// `null` (no rows at all) passes through untouched, matching the real
// backend's "nothing to report yet" case and ScanIndicator's
// `lastUpdatedAt === null` -> render-nothing branch. Otherwise the later of
// the rows' own max and the floor wins - see `MOCK_LAST_CHECKED_OFFSET_MS`'s
// comment for why the floor exists at all. Once `addSource` completes a scan,
// its row's real `Date.now()` `createdAt` (`scannedAt` below) wins over the
// floor, so the label moves to "a few seconds ago" - the mock's stand-in for
// the real backend now stamping `lastCheckedAt` on that same completion.
function resolveMockLastCheckedAt(rows: FeedFixtureRow[]): string | null {
  const latestRowCheckTime = latestCheckTimeAmongRows(rows);
  if (latestRowCheckTime === null) {
    return null;
  }
  const floor = mockLastCheckedFloor();
  return latestRowCheckTime > floor ? latestRowCheckTime : floor;
}

// Shared by the `getSources` case and the `addSource` case's own
// `sourcesLoaded` reply, so the "full fixture list plus whatever's been
// added this session" shape can't drift between the two call sites.
function buildSourcesLoadedPayload(): unknown[] {
  return [
    ...allFixtureSources.map((source) => ({
      id: source.id,
      name: source.name,
      url: source.url,
      status: source.status,
      createdAt: FIXTURE_SOURCE_CREATED_AT,
      isAutoGenerated: false,
      articles: source.articles,
    })),
    ...addedSourcesLoadedPayload,
  ];
}

// COM-638 task 5: fixture for `getArticleSummary` / `articleSummaryLoaded`.
// One fixed summary reused for whichever article was clicked - the panel
// under test (ArticleSummaryPanel) renders every one of these fields;
// `articleUrl` is echoed back per-request (see the `getArticleSummary` case
// below), not read off this constant, since the panel keys its "which
// article" state on the request, not on the fixture.
//
// COM-641 task 3: `financialInfo` is deliberately OMITTED (not just an empty
// string - `summarySections.ts`'s `paragraphSection` treats both the same via
// `!value`, but omitting is the more honest stand-in for "the LLM never
// returned this field", which is the real-world case the AC's "Not
// specified" wording covers) so exactly one of the eight AC sections has
// something for the e2e suite (Task 3) to assert "Not specified" against -
// every other field stays populated so the fixture still exercises every
// section kind (paragraph/list/links).
const fixtureArticleSummary: ArticleSummary = {
  articleUrl: '',
  publicationDate: '2026-07-15',
  authorInfo: 'European Banking Authority (EBA)',
  organizations: ['Banks', 'Payment institutions'],
  keyPoints: [
    'Introduces a new tiered capital buffer for cross-border banks.',
    'Clarifies the scope of the AML customer due diligence obligations.',
  ],
  actionsRequired: ['Update internal AML policies by the next reporting cycle.'],
  importantDates: ['Effective date: 2026-10-01'],
  // COM-641: jurisdiction and relatedDocuments are the new AC fields
  // (summarySections.ts) - a real-looking `relatedDocuments` entry (title +
  // url, both required to survive parsing on the backend -
  // articleSummaryFields.ts) so the panel's "links" section kind is
  // exercised by the fixture, not just documented. authorInfo above (a
  // pre-existing field) doubles as the "author or issuing authority" AC
  // field per the COM-641 correction - there is no separate issuingAuthority
  // field.
  jurisdiction: 'European Union',
  relatedDocuments: [
    { title: 'Consultation Paper CP/2026/12 on AML Customer Due Diligence', url: 'https://www.eba.europa.eu/publications-and-media/press-releases/cp-2026-12' },
  ],
};

// COM-641 task 2: one fixture article deliberately has NO generated summary,
// so an e2e test (Task 3) can open it and reach the panel's no-summary
// state. Reuses an existing feed row's url instead of adding a 26th article,
// so the feed's dedup/pagination fixture counts (26 raw rows -> 25 articles,
// see `fixtureArticleFeedRawRows`'s own comment) stay exactly as documented
// and every existing count-based assertion is unaffected.
const NO_SUMMARY_ARTICLE_URL = fixtureArticleFeedRows[15].url; // 'FCA handbook notice 112'

// COM-647 Task 3: fixture data for the Sources Settings table
// (`getManagedSources`/`managedSourcesLoaded`). Deliberately its own,
// SEPARATE fixture universe from `allFixtureSources`/`feedFixtureSources`
// above - those back the All Articles feed and its Sources filter dropdown
// (a different table, a different lean query on the real backend but the
// SAME underlying `sources` rows there; kept as two independent fixture sets
// here, same as `addSource`'s own `addedSourcesLoadedPayload` already is
// kept independent of this file's other fixtures).
//
// 30 generated rows (names cycled from `MANAGED_SOURCE_NAMES` below, one
// "(Archive)" variant per name so 15 names produce 30 distinct rows) plus 3
// hand-authored special cases the AC/design spec call out explicitly:
// `ms-unmonitored` (isMonitored: false, so the Active/Inactive toggle has a
// real "Inactive" row to render), `ms-invalid` (status: 'error', the
// "Invalid source" badge Task 4 renders), and `ms-unnamed` (name: '', so the
// table's name column/search has a real no-name row, same reasoning as
// `unnamedFixtureSource` above). 33 total, comfortably past the "30+, so
// endless scroll has something to page through" the brief asks for.
const MANAGED_SOURCE_NAMES = [
  'EBA Updates',
  'ESMA Updates',
  'FCA Updates',
  'BaFin Updates',
  'European Central Bank',
  'UK Treasury',
  'European Commission',
  'FINMA',
  'AMF France',
  'CONSOB Italy',
  'Bank of England',
  'CSSF Luxembourg',
  'DNB Netherlands',
  'Central Bank of Ireland',
  'European Parliament',
];

// Fixed base timestamp (not `Date.now()`), same determinism reasoning as
// `FEED_BASE_CREATED_AT` above - one calendar day apart per generated row so
// date-range filtering has real, distinct days to filter across.
const MANAGED_SOURCES_BASE_CREATED_AT = Date.parse('2026-01-01T00:00:00.000Z');

function managedSourceCreatedAt(index: number): string {
  return new Date(MANAGED_SOURCES_BASE_CREATED_AT + index * 24 * 60 * 60 * 1000).toISOString();
}

const managedSourcesFixtureGenerated: ManagedSource[] = MANAGED_SOURCE_NAMES.flatMap((name, nameIndex) => {
  return [0, 1].map((cycle): ManagedSource => {
    const index = nameIndex * 2 + cycle;
    return {
      id: `ms-${index + 1}`,
      name: cycle === 0 ? name : `${name} (Archive)`,
      url: `https://example.org/managed-sources/source-${index + 1}`,
      status: 'completed',
      isMonitored: true,
      createdAt: managedSourceCreatedAt(index),
    };
  });
});

const managedSourcesFixtureSpecialCases: ManagedSource[] = [
  {
    id: 'ms-unmonitored',
    name: 'Retired Regulator Feed',
    url: 'https://example.org/managed-sources/retired-regulator-feed',
    status: 'completed',
    isMonitored: false,
    createdAt: managedSourceCreatedAt(30),
  },
  {
    id: 'ms-invalid',
    name: 'Unreachable Regulator Feed',
    url: 'https://example.org/managed-sources/unreachable-regulator-feed',
    status: 'error',
    isMonitored: true,
    createdAt: managedSourceCreatedAt(31),
  },
  {
    id: 'ms-unnamed',
    name: '',
    url: 'https://example.org/managed-sources/unnamed-regulator-feed',
    status: 'completed',
    isMonitored: true,
    createdAt: managedSourceCreatedAt(32),
  },
];

// Module-scoped mutable state (not sessionStorage-backed, same reasoning as
// `addedSourceSeq`/`addedFeedRows` above - nothing here needs to survive a
// `page.reload()`, so a fresh module instance per test is the right
// lifetime): `updateSource`/`setSourceMonitored`/`deleteSources`/`addSources`
// below all mutate this array, and `getManagedSources` always reads its
// CURRENT contents, so a page reload gets a clean slate and effects from one
// test can never leak into the next module instance.
let managedSourcesState: ManagedSource[] = [...managedSourcesFixtureGenerated, ...managedSourcesFixtureSpecialCases];

let addedManagedSourceSeq = 0;

function findManagedSourceByUrl(url: string, excludeId?: string): ManagedSource | undefined {
  return managedSourcesState.find((source) => source.url === url && source.id !== excludeId);
}

// Mirrors `SourcesService#addSource`'s `MAX_SOURCES_PER_PROJECT` (COM-639) -
// the same cap the real `addSources` batch handler checks against the
// WHOLE batch, not per row (see the `addSources` case below).
const MAX_MANAGED_SOURCES = 100;

/**
 * A minimal in-memory stand-in for a Socket.IO client socket, used when
 * REACT_APP_E2E=true. It answers the small set of requests SourcesContext
 * makes on mount from static fixtures, so Playwright tests can exercise the
 * real UI/context code with zero backend or DB dependency.
 */
export function createMockSocket() {
  const handlers = new Map<string, Handler[]>();

  const fire = (event: string, ...args: unknown[]) =>
    setTimeout(() => (handlers.get(event) || []).forEach((h) => h(...args)), 0);

  const socket = {
    connected: true,

    on(event: string, handler: Handler) {
      handlers.set(event, [...(handlers.get(event) || []), handler]);
      return socket;
    },

    off(event: string, handler?: Handler) {
      if (!handler) {
        handlers.delete(event);
        return socket;
      }
      handlers.set(event, (handlers.get(event) || []).filter((h) => h !== handler));
      return socket;
    },

    close() {
      // noop: nothing to tear down for the mock
    },

    disconnect() {
      // noop: nothing to tear down for the mock
    },

    emit(event: string, ...args: unknown[]) {
      switch (event) {
        case 'loadProjects':
          fire('projects', [fixtureProject]);
          break;

        case 'getArticlesFeed': {
          // Runs the raw client payload through the SAME normalizer the
          // backend uses (mirrored in ./feedRequest.ts) before it ever
          // reaches `buildArticlesFeedPage` - review round 1 (Important):
          // this used to do its own `data.limit ?? 20` / `data.offset ?? 0`
          // with no clamping, so `limit: 1000` returned every row and
          // `offset: -5` hit `Array.prototype.slice`'s negative-index
          // behaviour and returned the LAST five articles instead of
          // flooring to 0.
          const request = normalizeFeedRequest(args[0]);

          // `requestId` (review round 2): echoed verbatim (through the same
          // cap the real backend applies), same as `getArticlesFeed`'s
          // handler in backend/src/index.ts - it's SourcesContext's
          // correlation token, not a query parameter, so it's read straight
          // off the raw payload rather than through `normalizeFeedRequest`.
          const requestId = normalizeFeedRequestId((args[0] as Record<string, unknown> | undefined)?.requestId);

          // COM-638 task 5: `__E2E_EMPTY_ARTICLES_FEED__` swaps the row set
          // to empty, still going through the same dedup/search/sort/paginate
          // pipeline so `total`/`offset` stay shaped exactly like a real
          // empty project instead of a hand-rolled shortcut reply.
          const baseRows = wantsEmptyArticlesFeed ? [] : fixtureArticleFeedRawRows;

          // COM-638 fix: overlay sessionStorage's cleared-url set onto the
          // base rows on every request, rebuilding the feed's `isNew`
          // status from scratch rather than trusting mutated module state -
          // this is what makes the flip survive a `page.reload()`, which
          // re-evaluates this module (and `fixtureArticleFeedRawRows`) fresh.
          // A plain `.map` (not an in-place mutation) so `fixtureArticleFeedRawRows`
          // itself never changes shape across calls.
          const clearedUrls = readClearedArticleUrls();
          const rowsWithReadState =
            clearedUrls.size === 0
              ? baseRows
              : baseRows.map((row) => (clearedUrls.has(row.url) ? { ...row, isNew: false } : row));

          // COM-640: one extra row per successful `addSource` call this
          // session (see that case below) - appended after the read-state
          // overlay, which only ever touches `baseRows`' own urls.
          const rows = [...rowsWithReadState, ...addedFeedRows];

          fire('articlesFeedLoaded', {
            ...buildArticlesFeedPage(rows, request),
            requestId,
            lastUpdatedAt: resolveMockLastCheckedAt(rows),
          });
          break;
        }

        case 'getArticleSummary': {
          // `SourcesContext#getArticleSummary` matches replies by `url`
          // (`data.url === url`), so the fixture must be echoed back under
          // the requested article's own url, not a fixed one - otherwise
          // every e2e "View summary" click would hang exactly like the gap
          // this case was added to close (see regulations-frontend skill's
          // "Known gaps" note, since fixed).
          const data = args[0] as { projectId: string; url: string };
          const summary = data.url === NO_SUMMARY_ARTICLE_URL ? null : { ...fixtureArticleSummary, articleUrl: data.url };
          fire('articleSummaryLoaded', { url: data.url, summary });
          break;
        }

        case 'updateArticleStatus': {
          // COM-638 fix wave: restores the read/unread flip AllArticlesPage's
          // "View summary" drives via SourcesContext#markArticleRead.
          // Records the cleared url in sessionStorage (see the
          // `READ_ARTICLE_URLS_STORAGE_KEY` comment above for why - survives
          // a reload, still isolated per Playwright test) instead of
          // mutating `fixtureArticleFeedRawRows` in place; `getArticlesFeed`
          // above re-applies every recorded url on every request, so this
          // only ever needs to persist "read", never a specific isNew value.
          // Fire-and-forget, same as the real handler's per-source articles
          // event this page doesn't consume - no reply is fired here either.
          const data = args[0] as { sourceId: string; articleUrl: string; isNew: boolean };
          if (data.isNew === false) {
            recordArticleUrlRead(data.articleUrl);
          }
          break;
        }

        case 'getSources': {
          // COM-639: reintroduces this case - COM-638 task 8 removed it as
          // unreachable once the old sources screen (its only consumer at
          // the time) was deleted as dead code. The Sources filter
          // dropdown's `fetchFilterSources` (SourcesContext.tsx) is a new
          // consumer: it emits this and listens for `sourcesLoaded`, the
          // SAME event/shape the real backend answers with (`getSources`
          // handler -> `sendAllSourcesWithFullArticles` ->
          // `DatabaseService#getAllSourcesWithFullArticles`, see
          // backend/src/index.ts / dbService.ts). Serves the FULL fixture
          // list unfiltered - with articles and without, named and unnamed
          // alike - exactly like the real backend: "has at least one
          // article" is a client-side filter `fetchFilterSources` applies,
          // not something this event does itself. `isAutoGenerated: false`
          // is fixture-only filler (shape parity with the real payload) -
          // nothing here reads it. `articles` (fix wave) is each source's
          // OWN fixture array (see `allFixtureSources` above) rather than a
          // hardcoded `[]` - the predicate this fixture exists to exercise
          // reads that field directly.
          fire('sourcesLoaded', buildSourcesLoadedPayload());
          break;
        }

        case 'getProjectUsers':
          // Mirror the backend: user management is refused outright for
          // embedded sessions (COM-636). Without this the mock would answer
          // happily and an e2e could never catch the client asking for
          // something an embedded session may not have.
          if (isEmbedded()) {
            fire('error', { message: 'User management is not available in embedded mode' });
            break;
          }

          fire('projectUsersLoaded', []);
          break;

        case 'setProjectData': {
          // Onboarding wizard's Finish path (COM-637/COM-638). Missing this
          // case entirely used to make the wizard's save silently no-op in
          // e2e: SourcesContext's `projectDataUpdated` handler never fired,
          // so `selectedProject` kept pointing at the pre-onboarding fixture
          // (empty `companyType`) forever, and the settings page rendered
          // with nothing checked - reproducing the reported "settings page
          // empty right after onboarding" bug, but only in this mock. The
          // real backend (`backend/src/index.ts`'s `setProjectData` handler)
          // always echoes `projectDataUpdated` after the write; this mirrors
          // that. Echoes the same shape as `updateProjectData` below - both
          // handlers persist the same fields and read back through the same
          // `projectDataUpdated` event on the real backend too.
          const data = args[0] as { projectId: string; projectData: ClientData };
          fire('projectDataUpdated', { ...fixtureProject, ...data.projectData });
          break;
        }

        case 'updateProjectData': {
          // Settings-page (COM-638) save path. Mirrors the backend write by
          // echoing the fixture project merged with the submitted data, and
          // — unlike `setProjectData` — must NOT fire `autoCompleteStarted`;
          // a later e2e test asserts that asymmetry.
          const data = args[0] as { projectId: string; projectData: ClientData };
          fire('projectDataUpdated', { ...fixtureProject, ...data.projectData });
          break;
        }

        case 'startSourceDiscovery':
          // Explicit source discovery (COM-637). Fires the started/finished
          // pair so e2e tests can tell "discovery ran" apart from
          // `updateProjectData` above, which deliberately does not.
          fire('autoCompleteStarted');

          if (wantsDiscoverySourceError) {
            // COM-637 task 8: a per-source failure (mirrors the backend's
            // `handleAutoGeneratedSource` emitting `error` for one bad
            // source in `backend/src/index.ts`) fired partway through the
            // run, well before `autoCompleteFinished` - real delays, not
            // `fire`'s 0ms, so a test can observe the dialog still open in
            // the gap between the two. The gap between `error` and the
            // "still open" check has to clear MUI's Dialog exit transition
            // (~225ms) - otherwise a regression that closes the dialog on
            // `error` could still read as "visible" mid-fade-out and the
            // test would false-pass.
            setTimeout(() => fire('error', { message: 'Failed to add auto-generated source' }), 150);
            setTimeout(() => fire('autoCompleteFinished'), 1500);
            break;
          }

          fire('autoCompleteFinished');
          break;

        case 'addSource': {
          const data = args[0] as { name: string; url: string; projectId: string };

          // COM-640 fix wave (Critical 1): mirrors backend/src/index.ts's
          // `addSource` guard exactly - `url` required, `name` deliberately
          // NOT checked (optional per the Jira AC). Before this fix the mock
          // had no guard at all, so the e2e test asserting "an empty name
          // succeeds" passed for a reason that had nothing to do with the
          // real backend's behaviour (which, before this same fix wave,
          // rejected it) - this line is what lets that test actually catch a
          // regression that reintroduces a name requirement on either side.
          //
          // Fix wave (re-review, coverage gap): now calls the extracted
          // `validateAddSourceInput` (./addSourceInput.ts) - the same
          // function pinned by that module's own unit test and mirrored by
          // backend/src/lib/addSourceInput.ts (used by the real handler) -
          // rather than re-implementing the check inline here, so this
          // e2e-only copy of the rule cannot silently drift from either.
          const addSourceValidationError = validateAddSourceInput(data);
          if (addSourceValidationError) {
            setTimeout(() => fire('error', { message: addSourceValidationError }), 50);
            break;
          }

          // COM-640 Task 3 hook: see the `__E2E_ADD_SOURCE_DUPLICATE__`
          // declaration above. Mirrors the real backend's duplicate-URL
          // refusal exactly (`socket.emit('error', { message: 'This URL
          // already saved' })`, backend/src/index.ts) - no `sourceAdded`
          // fires at all, since the real handler returns before creating
          // anything. A short delay (not `fire`'s synchronous 0ms) so a
          // test can observe the scanning indicator's optimistic `started`
          // state before it gets unwound by this refusal.
          if (wantsAddSourceDuplicate) {
            setTimeout(() => fire('error', { message: 'This URL already saved' }), 50);
            break;
          }

          addedSourceSeq += 1;
          const sourceId = `added-source-${addedSourceSeq}`;
          const articleUrl = `https://example.org/articles/added-${addedSourceSeq}`;
          // Real wall-clock time (not a fixed fixture constant, unlike every
          // other timestamp in this file) - this is the mock's stand-in for
          // the real backend stamping `Source.lastCheckedAt` the moment this
          // scan reaches a terminal status, so `latestCheckTimeAmongRows`
          // needs it to be newer than every existing fixture row for the
          // "Last updated" label to visibly move to "a few seconds ago" once
          // the scan finishes.
          const scannedAt = new Date().toISOString();

          fire('sourceAdded', { id: sourceId, name: data.name, url: data.url, status: 'pending' });

          // Real (non-zero) delay before `sourcesLoaded`, same technique
          // `startSourceDiscovery` above uses for its own started/finished
          // pair - mirrors the backend awaiting `processContentAsync`
          // between the two emits, and keeps the gap long enough for the
          // scanning indicator to be observably visible in e2e rather than
          // flashing for a single tick.
          setTimeout(() => {
            addedFeedRows.push({
              url: articleUrl,
              title: 'Newly discovered regulatory update',
              createdAt: scannedAt,
              updateDate: null,
              relevance: 'Relevant because it was just found by the new source',
              isNew: true,
              sourceId,
              sourceName: data.name,
              sourceUrl: data.url,
            });

            addedSourcesLoadedPayload.push({
              id: sourceId,
              name: data.name,
              url: data.url,
              status: 'completed',
              createdAt: scannedAt,
              isAutoGenerated: false,
              articles: [{ url: articleUrl }],
            });

            fire('sourcesLoaded', buildSourcesLoadedPayload());
          }, 800);

          break;
        }

        case 'getManagedSources': {
          // COM-647 Task 3: runs the raw payload through the SAME normalizer
          // the backend uses (mirrored in ./sourceListQuery.ts), then the
          // same filter/sort/paginate pipeline (./managedSourcesMock.ts) -
          // same technique `getArticlesFeed` above already uses for its own
          // normalizer/pipeline pair.
          const query = normalizeSourceListQuery(args[0]);
          const requestId = normalizeFeedRequestId((args[0] as Record<string, unknown> | undefined)?.requestId);

          // `__E2E_EMPTY_MANAGED_SOURCES__`: read from `[]` instead of the
          // real fixture set, same technique `wantsEmptyArticlesFeed` uses
          // for the feed above - `buildManagedSourcesPage` still runs (so
          // `total`/`offset` stay shaped correctly at 0), it just has
          // nothing to filter/sort/paginate.
          const sourceRows = wantsEmptyManagedSources ? [] : managedSourcesState;
          fire('managedSourcesLoaded', { ...buildManagedSourcesPage(sourceRows, query), requestId });
          break;
        }

        case 'updateSource': {
          // COM-647 Task 3 fix round (finding 1): mirrors backend/src/index.ts's
          // `updateSource` handler AND `SourcesService#updateSource` in their
          // ACTUAL order - the shared `validateAddSourceInput` guard (handler,
          // before the service is ever called), then the duplicate-url
          // refusal, THEN "source not found". The duplicate-url lookup runs
          // UNCONDITIONALLY FIRST in the service, before it ever checks
          // whether `id` itself resolves to a real row (`getSourceByUrlAndProject`
          // is called, and can throw, before `db.updateSource(id, ...)` is
          // reached at all) - so a call with a non-existent sourceId AND a url
          // that duplicates another row must get "This URL already saved",
          // never "Source not found". This file previously checked not-found
          // first, which only happened to match the backend when the two
          // refusal conditions never overlapped in the same call.
          const data = args[0] as { sourceId: string; name?: string; url: string };

          // Fix wave (2026-08-21, gap 1): matches the real backend's own
          // order - `requireProjectOwner` runs as a socket middleware/guard
          // BEFORE the handler's own body (and therefore before any payload
          // validation) ever executes.
          if (wantsViewerRole) {
            setTimeout(() => fire('error', { message: VIEWER_MUTATION_REFUSED_MESSAGE }), 50);
            break;
          }

          const validationError = validateAddSourceInput({ name: data.name, url: data.url });
          if (validationError) {
            setTimeout(() => fire('error', { message: validationError }), 50);
            break;
          }

          if (findManagedSourceByUrl(data.url, data.sourceId)) {
            setTimeout(() => fire('error', { message: 'This URL already saved' }), 50);
            break;
          }

          const existing = managedSourcesState.find((source) => source.id === data.sourceId);
          if (!existing) {
            setTimeout(() => fire('error', { message: 'Source not found' }), 50);
            break;
          }

          // `data.name ?? existing.name` (not `data.name || existing.name`):
          // an explicit empty string is a deliberate "clear the name"
          // instruction (COM-640's rule - `name` is optional, not
          // non-empty), and must survive; only an actually `undefined` name
          // falls back to the existing value. Mirrors
          // `DatabaseService#updateSource`'s own `data.name !== undefined`
          // check.
          const updated: ManagedSource = { ...existing, name: data.name ?? existing.name, url: data.url };
          managedSourcesState = managedSourcesState.map((source) => (source.id === updated.id ? updated : source));

          setTimeout(() => fire('sourceUpdated', updated), 50);
          break;
        }

        case 'setSourceMonitored': {
          const data = args[0] as { sourceId: string; isMonitored: boolean };

          // Fix wave (2026-08-21, gap 1): see `updateSource`'s own comment
          // above for why this runs before any other check in the case.
          if (wantsViewerRole) {
            setTimeout(() => fire('error', { message: VIEWER_MUTATION_REFUSED_MESSAGE }), 50);
            break;
          }

          if (typeof data.isMonitored !== 'boolean') {
            setTimeout(() => fire('error', { message: 'isMonitored must be a boolean' }), 50);
            break;
          }

          const existing = managedSourcesState.find((source) => source.id === data.sourceId);
          if (!existing) {
            setTimeout(() => fire('error', { message: 'Source not found' }), 50);
            break;
          }

          const updated: ManagedSource = { ...existing, isMonitored: data.isMonitored };
          managedSourcesState = managedSourcesState.map((source) => (source.id === updated.id ? updated : source));

          setTimeout(() => fire('sourceMonitoredUpdated', updated), 50);
          break;
        }

        case 'deleteSources': {
          // COM-647 Task 3: cross-project safety (the real
          // `DatabaseService#deleteSources`'s `deleteMany` WHERE clause
          // scoping to the caller's own project) is NOT mockable here - this
          // fixture universe only ever represents ONE project's sources, so
          // there is no "another project's row" for a malicious id to
          // collide with. Noted, not simulated, per the brief.
          const data = args[0] as { sourceIds: string[] };

          // Fix wave (2026-08-21, gap 1): see `updateSource`'s own comment
          // above for why this runs before any other check in the case.
          if (wantsViewerRole) {
            setTimeout(() => fire('error', { message: VIEWER_MUTATION_REFUSED_MESSAGE }), 50);
            break;
          }

          if (!Array.isArray(data.sourceIds) || data.sourceIds.length === 0) {
            setTimeout(() => fire('error', { message: 'No sources selected' }), 50);
            break;
          }

          // Fix wave (2026-08-21, gap 3): `__E2E_STALE_DELETE__` simulates
          // the exact race `dbService.ts#deleteSources`'s own comment
          // documents - a co-owner already deleted the selected row(s)
          // between the confirm dialog and this reply, so the delete affects
          // zero rows even though every id was valid when the user selected
          // it. Reports `deletedCount: 0` WITHOUT touching
          // `managedSourcesState` at all, so the page's post-delete refetch
          // (SourcesSettingsPage.tsx's `confirmDelete`) shows the rows
          // exactly as they were. This is what pins Important 3's fix: the
          // OLD code spliced `rows` by the REQUESTED `sourceIds` regardless
          // of `deletedCount`, which would have made those rows vanish from
          // the table here even though nothing was actually deleted - the
          // current refetch-from-source-of-truth code does not.
          if (wantsStaleDelete) {
            setTimeout(() => fire('sourcesDeleted', { deletedCount: 0, sourceIds: data.sourceIds }), 50);
            break;
          }

          const toDelete = new Set(data.sourceIds);
          const beforeCount = managedSourcesState.length;
          managedSourcesState = managedSourcesState.filter((source) => !toDelete.has(source.id));
          const deletedCount = beforeCount - managedSourcesState.length;

          setTimeout(() => fire('sourcesDeleted', { deletedCount, sourceIds: data.sourceIds }), 50);
          break;
        }

        case 'addSources': {
          // COM-647 Task 3 fix round (finding 2): mirrors the backend's
          // ACTUAL sequence across both `backend/src/index.ts`'s handler and
          // `SourcesService#addSources`, not a single-pass approximation of
          // it - the two diverge on cases where a batch trips more than one
          // refusal at once:
          //   1. empty-batch check (handler)
          //   2. validate EVERY entry's shape, over the WHOLE array, before
          //      any service call at all (handler's own `for` loop) - so a
          //      malformed entry anywhere in the batch is refused as a
          //      validation error even if an earlier entry would also have
          //      tripped the cap or a duplicate.
          //   3. the `MAX_MANAGED_SOURCES` cap, against the whole batch
          //      (service, first thing) - so a batch that's simultaneously
          //      over-cap AND has a malformed entry is refused for
          //      VALIDATION, never the cap, since step 2 already returned.
          //   4. per-entry duplicate check, in order (service): in-batch
          //      `seenUrls` first, then against the existing fixture state -
          //      so `[duplicate-of-existing, malformed]` is refused for
          //      VALIDATION (step 2 catches the malformed entry at index 1
          //      before this loop, which would have flagged index 0's
          //      duplicate first, ever runs).
          // The previous single-pass loop interleaved validation with the
          // duplicate check per entry and ran the cap check BEFORE
          // validation, so both constructed cases above came out wrong
          // (limit/duplicate instead of validation) - see this file's own
          // new tests for both, pinned against this exact sequence.
          const data = args[0] as { entries: { name?: string; url: string }[] };

          // Fix wave (2026-08-21, gap 1): see `updateSource`'s own comment
          // above for why this runs before any other check in the case.
          if (wantsViewerRole) {
            setTimeout(() => fire('error', { message: VIEWER_MUTATION_REFUSED_MESSAGE }), 50);
            break;
          }

          if (!Array.isArray(data.entries) || data.entries.length === 0) {
            setTimeout(() => fire('error', { message: 'No sources to add' }), 50);
            break;
          }

          for (const entry of data.entries) {
            const validationError = validateAddSourceInput(entry);
            if (validationError) {
              setTimeout(() => fire('error', { message: validationError }), 50);
              return socket;
            }
          }

          if (managedSourcesState.length + data.entries.length > MAX_MANAGED_SOURCES) {
            setTimeout(() => fire('error', { message: 'Source limit reached (100 per project)' }), 50);
            break;
          }

          const seenUrls = new Set<string>();
          for (const entry of data.entries) {
            if (seenUrls.has(entry.url) || findManagedSourceByUrl(entry.url)) {
              setTimeout(() => fire('error', { message: 'This URL already saved' }), 50);
              return socket;
            }
            seenUrls.add(entry.url);
          }

          const created: ManagedSource[] = data.entries.map((entry) => {
            addedManagedSourceSeq += 1;
            return {
              id: `ms-added-${addedManagedSourceSeq}`,
              name: entry.name ?? '',
              url: entry.url,
              // Fix wave (review, Item 5): the real backend
              // (`dbService.ts#createSource`) writes 'in_progress' for a
              // freshly created source, never 'pending' - this file's own
              // job is parity with the backend, so this must match exactly.
              status: 'in_progress',
              isMonitored: true,
              createdAt: new Date().toISOString(),
            };
          });

          managedSourcesState = [...managedSourcesState, ...created];

          // The Settings page's own actionable reply - same technique
          // `addSource`'s `sourceAdded` uses above, a short (non-zero) delay
          // rather than `fire`'s synchronous 0ms.
          //
          // Fix wave (2026-08-21, gap 4): reconciling this against the
          // `status: 'in_progress'` parity fix right above - the real
          // backend (`backend/src/index.ts`'s `addSources` handler) DOES
          // fire a `sourceAdded` per created entry and one `sourcesLoaded`
          // for the whole batch (via `sendAllSourcesWithFullArticles`)
          // BEFORE this final `sourcesAdded`, and this mock still
          // deliberately fires neither. Unlike the `status` field (a value
          // every consumer of `managedSourcesLoaded`/`sourceUpdated` reads
          // and can assert on), those two events are a no-op for everything
          // this case's urls touch: `SourcesContext`'s `sourceAdded`/
          // `sourcesLoaded` listeners only ever affect `scanState`/
          // `scanningSourceCount` for a url `addSource`'s OWN single-source
          // flow registered via a `'started'` dispatch, which a batch add
          // never does (see `SourcesContext#addSources`'s own comment for
          // why that integration is out of scope). Firing them here would
          // only slow down every e2e test on this path for zero additional
          // coverage. This is no longer just asserted in a comment:
          // `SourcesContext.test.tsx`'s "does not affect scanningSourceCount
          // even when the real sourceAdded/sourcesLoaded sequence fires
          // first" test fires that exact real-backend sequence directly
          // against the context and pins that `scanningSourceCount` stays 0
          // throughout - so the declined half IS covered, just not by
          // routing it through this mock.
          setTimeout(() => fire('sourcesAdded', created), 50);
          break;
        }

        default:
          break;
      }

      return socket;
    },
  };

  // SourcesContext registers its 'connect' handler synchronously right after
  // creating the socket, before this task's callback runs, so this always
  // reaches a listener.
  fire('connect');

  return socket;
}
